import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import type { Duration } from 'dayjs/plugin/duration'
import { identity } from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import useLogger from '~/debug/logger'
import { useUserPrefs } from '~/hooks/useUserPrefs'
import { useEffectiveWindowFocus } from '~/hooks/useWindowFocus'

import type { Tuple } from '../types/arrays'
import type { DayTransform, TimeRange } from './types'

/**
 * Returns a memoized Dayjs object. Consecutive calls to the returned function will return the exact
 * same Dayjs object if the operation applied to the input date yields an output date that is the same
 * as the previous output date. This reference equality is valuable for preventing unnecessary
 * re-renders of components which might want to use the Dayjs object as a dependency in a hook.
 *
 * @param date - Input date that can be passed into the Dayjs constructor. Defaults to the current time.
 * @param operation - A function to transform the dayjs object.
 * @returns - The input date transformed by the provided function.
 */
export function useTimeMemo(date?: dayjs.ConfigType, operation: DayTransform = identity): Dayjs {
  const { tzDayjs } = useUserPrefs()
  const [memoDate, setMemoDate] = useState(() => operation(tzDayjs(date)))

  useEffect(() => {
    const newDate = operation(tzDayjs(date))
    if (!newDate.isSame(memoDate)) {
      setMemoDate(newDate)
    }
  }, [date, operation, memoDate, tzDayjs])

  return memoDate
}

/**
 * Calls `callback` with a fresh `dayjs()` every 5 seconds while the window is focused,
 * and once immediately when the window regains focus. The callback ref is kept current so
 * callers can close over changing props without stale-closure issues.
 *
 * Keeps ticking regardless of focus in fullscreen "monitor" mode (ENG-10448) — this clock
 * is what re-anchors query args for widgets that don't poll at all (uptime summary/trend,
 * chargers needing attention, and the transaction-backed tiles), so gating it would leave
 * half the dashboard frozen on a wall display.
 */
export function useNowEffect(callback: (now: Dayjs) => void) {
  const isFocused = useEffectiveWindowFocus()
  const focusRef = useRef(isFocused)
  const callbackRef = useRef(callback)
  callbackRef.current = callback

  useEffect(() => {
    focusRef.current = isFocused
    if (isFocused) callbackRef.current(dayjs())
  }, [isFocused])

  useEffect(() => {
    const intervalId = setInterval(() => focusRef.current && callbackRef.current(dayjs()), 5_000)
    return () => clearInterval(intervalId)
  }, [])
}

/**
 * Returns a memoized Dayjs representing "now", updated every 5 seconds while the window is focused.
 * Rounds down to the nearest minute by default; pass a custom `operation` to transform differently.
 */
export function useNowMemo(operation: DayTransform = (d) => d.startOf('minute')): Dayjs {
  const [now, setNow] = useState(() => dayjs())
  useNowEffect(setNow)
  return useTimeMemo(now, operation)
}

export type TimestampColorOptions = {
  warningMinutes?: number
  errorMinutes?: number
}

export type TimestampLevel = 'fresh' | 'warning' | 'error'

export type TimestampColorResult = {
  /** MUI color path for the timestamp text. */
  color: string
  /** Discrete staleness level — `'fresh'` when within thresholds, `'warning'` past `warningMinutes`, `'error'` past `errorMinutes`. */
  level: TimestampLevel
  /** Convenience derived flag: true when the level is anything other than `'fresh'`. */
  isStale: boolean
}

const LEVEL_COLORS: Record<TimestampLevel, string> = {
  fresh: 'text.primary',
  warning: 'warning.main',
  error: 'error.main'
}

function getTimestampLevel(dateTime: dayjs.ConfigType, now: Dayjs, options?: TimestampColorOptions): TimestampLevel {
  const { warningMinutes, errorMinutes } = options ?? {}
  const diff = now.diff(dayjs(dateTime), 'minute')
  if (errorMinutes != null && diff >= errorMinutes) return 'error'
  if (warningMinutes != null && diff >= warningMinutes) return 'warning'
  return 'fresh'
}

function resultFromLevel(level: TimestampLevel): TimestampColorResult {
  return { color: LEVEL_COLORS[level], level, isStale: level !== 'fresh' }
}

/**
 * Pure computation: returns the staleness level of a timestamp along with a derived MUI color
 * and an `isStale` convenience flag. Only applies warning/error levels for thresholds that are
 * explicitly provided.
 */
export function getTimestampColor(
  dateTime: dayjs.ConfigType,
  now: Dayjs,
  options?: TimestampColorOptions
): TimestampColorResult {
  return resultFromLevel(getTimestampLevel(dateTime, now, options))
}

/**
 * Returns the staleness level + matching color + isStale flag for a timestamp, re-evaluated
 * periodically. Only triggers a re-render when the level actually changes (at threshold
 * boundaries), not on every tick.
 */
export function useTimestampColor(dateTime: dayjs.ConfigType, options?: TimestampColorOptions): TimestampColorResult {
  const { warningMinutes, errorMinutes } = options ?? {}
  const [level, setLevel] = useState(() => getTimestampLevel(dateTime, dayjs(), { warningMinutes, errorMinutes }))

  useEffect(() => {
    setLevel(getTimestampLevel(dateTime, dayjs(), { warningMinutes, errorMinutes }))
  }, [dateTime, warningMinutes, errorMinutes])

  useNowEffect((now) => {
    const next = getTimestampLevel(dateTime, now, { warningMinutes, errorMinutes })
    setLevel((prev) => (prev === next ? prev : next))
  })

  return resultFromLevel(level)
}

type TimeRangeOptions = {
  duration?: number | Duration
  start?: dayjs.ConfigType | 'today'
  end?: dayjs.ConfigType | 'today'
  unit: dayjs.OpUnitType
}

type TimeRangeControls = {
  fromOptions(options: TimeRangeOptions): void
  fromTuple(tuple: Tuple<dayjs.ConfigType>): void
}

export type TimeRangeWrapper = {
  timeRange: TimeRange
  controls: TimeRangeControls
}

/**
 * Generates a `TimeRange` object based on the provided options, and returns convenient methods for
 * updating the range.
 */
export function useTimeRange(options: TimeRangeOptions): TimeRangeWrapper {
  const { tzDayjs } = useUserPrefs()
  const logger = useLogger('useTimeRange')
  const optionsError = useMemo(() => validateOptions(options), [options])
  if (optionsError) {
    logger.error(optionsError)
  }

  // Parse the options and round to the start of the specified unit (or day by default)
  const { unit } = options
  const getCurrentTime = useCallback(() => tzDayjs().startOf(unit), [tzDayjs, unit])
  const [timeRange, setTimeRange] = useState<TimeRange>(() => {
    const [start, end] = parseRangeOptions(options, getCurrentTime())
    return { from: start.startOf(unit), to: end.endOf(unit) }
  })

  // Provides convenient methods for setting the time range
  const controls = useMemo<TimeRangeControls>(() => {
    return {
      fromOptions(options: TimeRangeOptions) {
        const [start, end] = parseRangeOptions(options, getCurrentTime())
        setTimeRange({ from: start.startOf(unit), to: end.endOf(unit) })
      },

      fromTuple([from, to]: Tuple<dayjs.ConfigType>) {
        if (dayjs(from).isValid() && dayjs(to).isValid())
          setTimeRange({ from: dayjs(from).startOf(unit), to: dayjs(to).endOf(unit) })
      }
    }
  }, [getCurrentTime, unit])

  return { timeRange, controls }
}

/**
 * For a `TimeRangeOptions` object to be valid, it must be unambiguous. This means that exactly 2 of
 * the 3 properties must be provided. If all 3 are provided the object will be considered invalid,
 * even if the values are consistent.
 */
function validateOptions({ duration, start, end }: TimeRangeOptions) {
  if (duration && start && end) {
    return 'Cannot specify both duration and start/end'
  }

  const providedOptions = [duration, start, end].filter((o) => o !== undefined)
  if (providedOptions.length !== 2) {
    return 'Must specify exactly 2 of `duration`, `start`, and `end`'
  }
}

/**
 * Parses the provided `TimeRangeOptions` object into a tuple of Dayjs objects representing the
 * start and end of the range. If the `start` or `end` properties are set to `'today'`, they will be
 * replaced with the current time.
 *
 * If the options are invalid, the current time will be used for both the start and end of the range.
 * This is somewhat arbitrary. It's intended to prevent inadvertently making API requests with hazardously
 * large date ranges.
 */
function parseRangeOptions(options: TimeRangeOptions, now: Dayjs): Tuple<Dayjs> {
  const { duration, start: startArg, end: endArg } = options
  const start = startArg === 'today' ? now : startArg
  const end = endArg === 'today' ? now : endArg

  if (start && end) {
    return [dayjs(start), dayjs(end)]
  } else if (start && duration) {
    const startDay = dayjs(start)
    if (typeof duration === 'number') {
      return [startDay, startDay.add(duration, 'days')]
    } else {
      return [startDay, startDay.add(duration)]
    }
  } else if (end && duration) {
    const endDay = dayjs(end)
    if (typeof duration === 'number') {
      return [endDay.subtract(duration, 'days'), endDay]
    } else {
      return [endDay.subtract(duration), endDay]
    }
  } else {
    return [now, now]
  }
}
